"use client";

import React from "react";
import { Sparkles, Heart, Tag, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const treatments = [{
  id: "beauty",
  title: "Advanced Beauty",
  description: "Meticulous care for your natural features, from precision brow sculpting to restorative nail rituals in our Brooke sanctuary.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/eyebrow_sculpting_beauty_treatment.png",
  reverse: false
}, {
  id: "aesthetics",
  title: "Clinical Aesthetics",
  description: "Sophisticated non-surgical treatments designed to enhance your natural architecture with clinical precision and artistic vision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/botox_lady_aesthetics_procedure_new.png",
  reverse: true
}, {
  id: "massage",
  title: "Holistic Massage",
  description: "A sensory journey of total restoration, blending ancient techniques with modern therapeutic touch to release deep-seated tension.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/serene_massage_therapy_session.png",
  reverse: false
}];
const treatmentTabs = [{
  treatmentId: "beauty",
  tab: "details",
  content: "Our beauty rituals include precision brow mapping, custom tinting, and luxury manicures using high-performance botanicals and medical-grade sterilization."
}, {
  treatmentId: "beauty",
  tab: "aftercare",
  content: "Maintain your results by avoiding heat treatments and oils for 24 hours. We recommend daily hydration with our signature cuticle and brow oils."
}, {
  treatmentId: "beauty",
  tab: "pricing",
  content: "Signature Brow Sculpt: £35. Luxury Gel Manicure: £45. Bespoke beauty packages are available for regular maintenance and events."
}, {
  treatmentId: "aesthetics",
  tab: "details",
  content: "Advanced skin rejuvenation and injectables performed by qualified practitioners. We focus on subtle, natural enhancements that honor your unique facial structure."
}, {
  treatmentId: "aesthetics",
  tab: "aftercare",
  content: "Arrive makeup-free. Post-treatment, avoid strenuous exercise and direct sunlight for 48 hours. Follow your personalized dermal recovery plan provided."
}, {
  treatmentId: "aesthetics",
  tab: "pricing",
  content: "Consultations from £50 (redeemable against treatment). Skin Boosters from £180. Bespoke rejuvenation plans priced upon assessment."
}, {
  treatmentId: "massage",
  tab: "details",
  content: "Choose from Deep Tissue, Swedish, or Hot Stone therapy. Each session is tailored to your physical needs and emotional well-being in a tranquil setting."
}, {
  treatmentId: "massage",
  tab: "aftercare",
  content: "Drink plenty of water to assist lymphatic drainage. Rest and avoid caffeine for several hours to allow the nervous system to remain in a state of calm."
}, {
  treatmentId: "massage",
  tab: "pricing",
  content: "60-Minute Restoration: £65. 90-Minute Sanctuary Session: £90. Course of 5 treatments available with a 10% loyalty reduction."
}];
const tabOptions = [{
  id: "details",
  label: "Details",
  icon: Sparkles
}, {
  id: "aftercare",
  label: "Aftercare",
  icon: Heart
}, {
  id: "pricing",
  label: "Pricing",
  icon: Tag
}];
export default function SignatureRituals() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="100890" id="the-beauty-menu" className="relative w-full bg-background py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/20 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/20 blur-[100px]" />
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="font-serif text-4xl md:text-6xl text-foreground mb-6 tracking-tight">
            The Beauty Menu
          </h2>
          <p className="text-xl text-muted-foreground font-sans leading-relaxed tracking-normal">
            Discover a curated collection of rituals at Natalia's Beauty. From clinical precision to holistic serenity, we offer a high-end sanctuary in Brooke for your transformation.
          </p>
        </div>

        <div className="flex flex-col gap-16 lg:gap-24">
          {treatments.map((treatment, i) => <Card key={treatment.id} data-index={i} className={`flex flex-col ${treatment.reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-8 lg:gap-12 p-6 lg:p-10 rounded-[2.5rem] bg-card/40 backdrop-blur-xl border-border shadow-2xl shadow-foreground/5 overflow-hidden border`}>
              {}
              <div className="w-full lg:w-1/2 relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:min-h-[450px] group">
                <div className="absolute inset-0 bg-secondary/10 mix-blend-overlay z-10 transition-opacity duration-500 group-hover:opacity-0" />
                <img src={treatment.image} alt={treatment.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
              </div>

              {}
              <div className="w-full lg:w-1/2 flex flex-col justify-center gap-8 py-4 lg:py-8">
                <div>
                  <h3 className="text-4xl lg:text-5xl font-serif text-foreground mb-4 italic">
                    {treatment.title}
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed font-sans">
                    {treatment.description}
                  </p>
                </div>

                <Tabs defaultValue="details" className="w-full">
                  <TabsList className="w-full grid grid-cols-3 bg-muted/40 p-1.5 rounded-2xl">
                    {tabOptions.map((option, j) => {
                  const Icon = option.icon;
                  return <TabsTrigger key={option.id} value={option.id} data-index={j} className="rounded-xl py-2.5 text-xs font-sans font-semibold tracking-widest uppercase transition-all data-[state=active]:bg-background data-[state=active]:shadow-md data-[state=active]:text-foreground">
                          <Icon className="w-3.5 h-3.5 mr-2 hidden sm:inline-block text-ring" />
                          {option.label}
                        </TabsTrigger>;
                })}
                  </TabsList>

                  {tabOptions.map((option, k) => {
                const tabData = treatmentTabs.find(t => t.treatmentId === treatment.id && t.tab === option.id);
                return <TabsContent key={option.id} value={option.id} data-index={k} className="mt-6 text-muted-foreground leading-relaxed min-h-[120px] text-base font-sans animate-in fade-in slide-in-from-bottom-2 duration-700">
                        {tabData?.content}
                      </TabsContent>;
              })}
                </Tabs>

                <div className="pt-2">
                  {i === 1 ? <Link to="https://content-man.10web.cloud/aesthetics/">
                      <Button variant="default" size="lg" className="rounded-lg px-10 py-7 text-xs font-sans font-bold tracking-widest uppercase bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground shadow-lg shadow-foreground/5 transition-all hover:shadow-xl group border border-border">
                        Explore {treatment.title.split(" ")[1]}
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-2" />
                      </Button>
                    </Link> : i === 2 ? <Link to="https://content-man.10web.cloud/massage/">
                      <Button variant="default" size="lg" className="rounded-lg px-10 py-7 text-xs font-sans font-bold tracking-widest uppercase bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground shadow-lg shadow-foreground/5 transition-all hover:shadow-xl group border border-border">
                        Explore {treatment.title.split(" ")[1]}
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-2" />
                      </Button>
                    </Link> : <Link to="https://nataliasbeauty.com/beauty-menu/">
                      <Button variant="default" size="lg" className="rounded-lg px-10 py-7 text-xs font-sans font-bold tracking-widest uppercase bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground shadow-lg shadow-foreground/5 transition-all hover:shadow-xl group border border-border">
                        Explore {treatment.title.split(" ")[1]}
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-2" />
                      </Button>
                    </Link>}
                </div>
              </div>
            </Card>)}
        </div>
      </div>
    </section>;
}